import * as React from 'react'
import * as classnames from 'classnames'
import { Link } from 'react-router-dom'
import { Site } from '@envato/foundation-types'

import { IItem } from '../../../../typings/searchResults'
import { reportBlockClick } from '../../../../common/analytics/homePageAnalytics'

import SearchLink from '../../../containers/searchLinkContainer'
import { IHomePageBlockComponent } from '../../../../typings/pageBlocks'
import ItemCard from '../../itemCard'
import Card from '../../categoryPage/elementsPromoItemsBlock/card/card'
import ItemCardsLayout from '../../itemCardsLayout'
import Button from '../../button'

import * as styles from './itemsShowcaseBlock.module.css'

interface ICta {
  url: string
  title: string
  urlSearchParams?: undefined
}

interface ISearchCta {
  title: string
  urlSearchParams: {
    [key: string]: string
  }
  url?: undefined
}

type ReportItemClickFunction = (
  index: number,
  item: IItem,
  listName: string
) => null

interface IProps extends IHomePageBlockComponent {
  data: IItem[]
  displayProperties: {
    blockName: string
    testSelector: string
    evenGrid: boolean
  }
  reportItemClick: ReportItemClickFunction
  title: string
  subtitle?: string
  cta?: ICta | ISearchCta
  currentSite: Site
}

const getCtaButton = ({
  cta,
  blockName,
  cssClass
}: {
  cta?: ICta | ISearchCta
  blockName: string
  cssClass: 'leftButton' | 'lowerButton'
}) => {
  if (!cta) {
    return null
  }

  const commonProps = {
    type: Button.Type.Primary,
    className: styles[cssClass],
    onClick: () => {
      reportBlockClick(blockName, {
        eventLabel: cta.title
      })
    }
  }

  let props

  if (cta.url) {
    props = {
      ...commonProps,
      renderAs: Link,
      to: cta.url
    }
  }

  if (cta.urlSearchParams) {
    props = {
      ...commonProps,
      renderAs: SearchLink,
      locationParams: cta.urlSearchParams
    }
  }

  return <Button {...props}>{cta.title}</Button>
}

const renderSimplifiedItemCard = (reportItemClick: ReportItemClickFunction) => (
  item: IItem,
  index: number
) => {
  return (
    <div className={styles.cardWrapper}>
      <Card
        {...item}
        imageRatios={{ previewRatio: 300 / 590 }}
        title={item.name}
        author={item.author_username}
        width={50}
        itemURL={item.url}
        showCTA={false}
        onClick={() =>
          reportItemClick(
            index,
            item,
            'Block interaction;Homepage: Bestsellers'
          )
        }
      />
    </div>
  )
}

const renderStandardItemCard = (blockName: string) => (
  item: IItem,
  index: number
) => (
  <ItemCardsLayout.CardArea key={`${blockName}-${item.id}`}>
    <ItemCard
      listName={`Homepage: ${blockName}`}
      item={item}
      key={item.id}
      index={index}
      viewType="grid"
    />
  </ItemCardsLayout.CardArea>
)

const ItemsShowcaseBlock = ({
  data,
  title,
  subtitle,
  cta,
  displayProperties,
  reportItemClick,
  currentSite
}: IProps) => {
  const { blockName, testSelector, evenGrid } = displayProperties
  const gridStyles = classnames(styles.grid, {
    [styles.evenGrid]: evenGrid
  })
  const showSimplifiedItemCard = currentSite === Site.photodune

  return (
    <section className={styles.root} data-test-selector={testSelector}>
      <div className={styles.wrapper}>
        <ItemCardsLayout viewType="grid" className={gridStyles}>
          <ItemCardsLayout.CardArea className={styles.textArea}>
            <div className={styles.textAreaInner}>
              <header>
                <h2 className={styles.title}>{title}</h2>
                {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
              </header>
              {getCtaButton({ cta, blockName, cssClass: 'leftButton' })}
            </div>
          </ItemCardsLayout.CardArea>
          {showSimplifiedItemCard
            ? data.map(renderSimplifiedItemCard(reportItemClick))
            : data.map(renderStandardItemCard(blockName))}
        </ItemCardsLayout>

        {getCtaButton({ cta, blockName, cssClass: 'lowerButton' })}
      </div>
    </section>
  )
}

export default ItemsShowcaseBlock
